import { useCallback, useEffect, useReducer } from 'react';
import {
  PowerBiReportDto,
  useGetOrganizationPowerBiReports,
  useGetSingleOrganizationPowerBiReport,
} from '@sib/api';
import { useAuthStore, useUserSetupStore } from '@sib/auth';

type PowerBiReportsState = {
  isLoading: boolean;
  isError: boolean;
  powerBiReports: PowerBiReportDto[];
};

type PowerBiReportsAction = Partial<PowerBiReportsState>;

const initialPowerBiReportsState: PowerBiReportsState = {
  isLoading: false,
  isError: false,
  powerBiReports: [],
};

const powerBiReportsReducer = (
  previousState: PowerBiReportsState,
  nextState: PowerBiReportsAction
): PowerBiReportsState => ({
  ...previousState,
  ...nextState,
});

interface UsePowerBiReportsOptions {
  organizationId?: string;
}

export const usePowerBiReports = (options?: UsePowerBiReportsOptions) => {
  const [powerBiReports, dispatch] = useReducer(
    powerBiReportsReducer,
    initialPowerBiReportsState
  );
  const { isAuthenticated } = useAuthStore();
  const currentOrganizationId = useUserSetupStore(
    state => state.organization.id
  );
  const organizationId = options
    ? options.organizationId
    : currentOrganizationId;

  const {
    data,
    isError: isListError,
    isFetched: isListFetched,
    isLoading: isListLoading,
  } = useGetOrganizationPowerBiReports({
    organizationId,
    options: {
      enabled: isAuthenticated && Boolean(organizationId),
    },
  });
  const { mutateAsync: fetchReportDetails } =
    useGetSingleOrganizationPowerBiReport();

  const fetchAllReportDetails = useCallback(async () => {
    if (!organizationId || !isAuthenticated || isListError || !data?.items)
      return dispatch({
        isLoading: false,
        isError: isListError,
        powerBiReports: [],
      });

    if (data.items.length === 0)
      return dispatch({ isLoading: false, isError: false, powerBiReports: [] });

    dispatch({ isLoading: true, isError: false });

    const reports = await Promise.allSettled(
      data.items.map(async ({ id }) =>
        fetchReportDetails({ reportId: id, organizationId })
      )
    );

    const fulfilledReports = reports
      .filter(
        (report): report is PromiseFulfilledResult<PowerBiReportDto> =>
          report.status === 'fulfilled'
      )
      .map(report => report.value);

    dispatch({
      powerBiReports: fulfilledReports,
      isLoading: false,
      isError: fulfilledReports.length === 0,
    });
  }, [data, fetchReportDetails, isAuthenticated, isListError, organizationId]);

  useEffect(() => {
    fetchAllReportDetails();
  }, [fetchAllReportDetails]);

  const isWaitingForList =
    isAuthenticated &&
    Boolean(organizationId) &&
    !isListFetched &&
    !isListError;
  const hasPendingDetails =
    isListFetched &&
    !isListError &&
    (data?.items.length ?? 0) > 0 &&
    powerBiReports.powerBiReports.length === 0 &&
    !powerBiReports.isError;

  return {
    isLoading:
      isWaitingForList ||
      isListLoading ||
      powerBiReports.isLoading ||
      hasPendingDetails,
    isError: isListError || powerBiReports.isError,
    powerBiReports: powerBiReports.powerBiReports,
  };
};
